// When did this person last have the app open? If it's been a while, Today
// shows "While you were out". Kept in localStorage (per device), updated
// every minute while the app is on screen.

import { useSyncExternalStore } from 'react';

const KEY = 'arlo.lastActive';
/** Away for at least this long before a handover is worth showing. */
export const AWAY_MS = 90 * 60_000;

function read(): number | undefined {
  try {
    const v = Number(localStorage.getItem(KEY));
    return Number.isFinite(v) && v > 0 ? v : undefined;
  } catch {
    return undefined;
  }
}

function write(t: number) {
  try {
    localStorage.setItem(KEY, String(t));
  } catch {
    /* ignore */
  }
}

/** The time away: from when the app was last used to when they came back. */
export interface Away {
  since: number;
  until: number;
}

let away: Away | undefined;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function arrive(now = Date.now()) {
  const last = read();
  if (last && now - last >= AWAY_MS) {
    away = { since: last, until: now };
    emit();
  }
  write(now);
}

let started = false;
/** Begin tracking (call once at app start, whatever screen opens first). */
export function startAwayTracking() {
  if (started || typeof document === 'undefined') return;
  started = true;
  arrive();
  setInterval(() => document.visibilityState === 'visible' && write(Date.now()), 60_000);
  document.addEventListener('visibilitychange', () => (document.visibilityState === 'visible' ? arrive() : write(Date.now())));
  window.addEventListener('pagehide', () => write(Date.now()));
}

/** The last time away from the app, if it was long enough to warrant a handover. */
export function useAway(): Away | undefined {
  startAwayTracking();
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => away,
  );
}

export function dismissAway() {
  away = undefined;
  emit();
}
